/**
 * PublicChatWidget — multi-step Rescigo Health AI chatbot (Iter 93).
 *
 * State machine steps:
 *   welcome        → 3-way branch (new / existing / ask rigo)
 *   new_phone      → collect phone → send OTP
 *   new_otp        → verify OTP
 *   new_details    → name / topic / area / initial message
 *   existing_phone → collect phone → send OTP
 *   existing_otp   → verify OTP (must be registered)
 *   existing_menu  → quick action menu for logged-in visitor
 *   ask_rigo       → freeform bot Q&A
 *   handoff        → visitor typed enough → we POST /public/chat/handoff
 *   queue          → conversation pending, polling status + messages
 *   live           → conversation "human", chatting with staff
 *   rating         → post-chat 1-5 rating + feedback
 *   closed         → thank-you screen
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  X, PaperPlaneRight, ArrowRight, ArrowLeft, Star, CheckCircle,
  UserPlus, User, ChatCircleText,
} from "@phosphor-icons/react";
import { RIGO_AVATAR_URL, RigoAvatar } from "./rigo";
import { linkify } from "../lib/linkify";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 2500;   // Iter 138 — faster live-refresh (was 4000)

const SERVICE_TOPICS = [
  { id: "new_service",   label: "Book a new pickup" },
  { id: "billing",       label: "Billing / invoices" },
  { id: "bag_delivery",  label: "Bag delivery / PIN" },
  { id: "support",       label: "Report an issue" },
  { id: "other",         label: "Something else" },
];

const QUICK_LINKS_EXISTING = [
  { id: "requests",   label: "My pickups",     to: "/me/requests" },
  { id: "invoices",   label: "My invoices",    to: "/me/invoices" },
  { id: "chat",       label: "Chat with team", handoff: true },
];

const DISMISS_KEY = "rescigo:public_chat_dismissed_at";
const DISMISS_HOURS = 6;   // FAB reappears after this many hours

// --------------------------------------------------------------
export default function PublicChatWidget() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  // Iter 186 — user-dismissible FAB. Persist for DISMISS_HOURS in
  // localStorage so the icon doesn't reappear on every navigation.
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return ts && (Date.now() - ts) < DISMISS_HOURS * 3600 * 1000;
  });
  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  // Iter 186 — chat is customer-facing only. Hide whenever any staff /
  // admin / field-staff token is present (they get support via the
  // topbar bell / staff app). Also hide on staff-only routes.
  const hasAnyStaffToken = typeof window !== "undefined" && (
    !!localStorage.getItem("rescigo_token")
    || !!localStorage.getItem("rescigo_staff_token")
    || !!sessionStorage.getItem("rescigo_staff_token")
  );
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const hideByPath = path.startsWith("/me") || path.startsWith("/staff-app")
      || path.startsWith("/dashboard") || path.startsWith("/customers")
      || path.startsWith("/operations") || path.startsWith("/crm")
      || path.startsWith("/finance") || path.startsWith("/settings")
      || path.startsWith("/reports") || path.startsWith("/gov")
      || path.startsWith("/partners") || path.startsWith("/inventory")
      || path.startsWith("/qr") || path.startsWith("/routes")
      || path.startsWith("/staff") || path.startsWith("/workforce")
      || path.startsWith("/admin") || path.startsWith("/adentry")
      || path.startsWith("/login") || path.startsWith("/emdispatch");

  useEffect(() => {
    if (hasAnyStaffToken || hideByPath) return;
    fetch(`${API}/public/chat/config`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => { if (d.enabled) setCfg(d); })
      .catch(() => {});
  }, [hasAnyStaffToken, hideByPath]);

  if (hasAnyStaffToken || hideByPath || !cfg || dismissed) return null;

  return (
    <>
      {!open && (
        <div className="fixed bottom-5 right-5 z-50 flex items-center gap-2">
          <button
            data-testid="public-chat-fab-tag"
            onClick={() => setOpen(true)}
            aria-hidden="true"
            className="hidden sm:flex items-center gap-1 bg-white border border-[#E5E2D9] shadow-lg pl-3 pr-4 py-2 rounded-full text-[#C25D38] text-xs font-semibold hover:bg-[#FFF7F3] transition-colors"
            style={{ animation: "rigo-tag-bounce 3.4s ease-in-out infinite" }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#2D5A27] animate-pulse"/>
            Ask Rigo
          </button>
          <div className="relative">
            <button
              data-testid="public-chat-fab"
              onClick={() => setOpen(true)}
              aria-label="Chat with Rigo"
              className="w-16 h-16 rounded-full bg-white ring-2 ring-[#C25D38]/60 shadow-2xl hover:scale-105 transition-transform overflow-hidden group"
            >
              <img src={RIGO_AVATAR_URL} alt="Rigo"
                   className="w-full h-full object-cover group-hover:scale-110 transition-transform"/>
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#C25D38] ring-2 ring-white animate-pulse"/>
            </button>
            {/* Iter 186 — user can dismiss the FAB for DISMISS_HOURS. */}
            <button
              data-testid="public-chat-fab-close"
              onClick={dismiss}
              aria-label="Hide Ask Rigo"
              title="Hide Ask Rigo for a while"
              className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-white border border-[#E5E2D9] text-[#5C5A55] hover:text-[#7A2E18] hover:border-[#C25D38] shadow-md flex items-center justify-center"
            >
              <X size={11} weight="bold"/>
            </button>
          </div>
          <style>{`
            @keyframes rigo-tag-bounce {
              0%, 92%, 100% { transform: translateY(0); }
              95% { transform: translateY(-4px); }
              97% { transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
      {open && <ChatPanel cfg={cfg} onClose={() => setOpen(false)}/>}
    </>
  );
}

// --------------------------------------------------------------
function ChatPanel({ cfg, onClose }) {
  const [step, setStep] = useState("welcome");
  const [ctx, setCtx] = useState({});   // phone, name, otp, dev_otp, customer, topic, ...
  const [session, setSession] = useState(null); // {token, conv_id}

  const goto = (next, patch = {}) => {
    setCtx(c => ({ ...c, ...patch }));
    setStep(next);
  };
  const reset = () => { setSession(null); setCtx({}); setStep("welcome"); };

  return (
    <div className="fixed inset-x-0 bottom-0 md:inset-auto md:bottom-5 md:right-5 md:w-[400px] z-50"
         data-testid="public-chat-panel">
      <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl overflow-hidden border border-[#E5E2D9] flex flex-col"
           style={{ maxHeight: "min(640px, 88vh)" }}>
        {/* Header */}
        <div className="p-4 bg-[#C25D38] text-white flex items-center gap-3">
          <RigoAvatar size={44} ring={false}/>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base leading-tight">{cfg.bot_name || "Rigo"}</div>
            <div className="text-[11px] opacity-90">
              {cfg.in_office_hours
                ? "Ask me anything · Office team online"
                : `Office ${cfg.office_start}–${cfg.office_end} · I can still help`}
            </div>
          </div>
          {step !== "welcome" && step !== "closed" && !session && (
            <button onClick={reset}
                    data-testid="public-chat-restart"
                    className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
                    title="Start over">
              <ArrowLeft size={16} weight="bold"/>
            </button>
          )}
          <button onClick={onClose}
                  data-testid="public-chat-close"
                  className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center">
            <X size={16} weight="bold"/>
          </button>
        </div>

        {/* Body — swap by step */}
        <div className="flex-1 overflow-y-auto bg-[#FAFAF7]">
          {step === "welcome"        && <WelcomeStep goto={goto} cfg={cfg}/>}
          {step === "new_phone"      && <PhoneStep goto={goto} ctx={ctx} kind="new"/>}
          {step === "new_otp"        && <OtpStep goto={goto} ctx={ctx} kind="new"/>}
          {step === "new_details"    && <NewDetailsStep goto={goto} ctx={ctx} setSession={setSession}/>}
          {step === "existing_phone" && <PhoneStep goto={goto} ctx={ctx} kind="existing"/>}
          {step === "existing_otp"   && <OtpStep goto={goto} ctx={ctx} kind="existing"/>}
          {step === "existing_menu"  && <ExistingMenu goto={goto} ctx={ctx} setSession={setSession}/>}
          {step === "ask_rigo"       && <AskRigoStep cfg={cfg} goto={goto}/>}
          {(step === "queue" || step === "live") && session && (
            <LiveChatStep step={step} setStep={setStep} session={session} ctx={ctx}/>
          )}
          {step === "rating" && session && (
            <RatingStep session={session} onDone={() => setStep("closed")}/>
          )}
          {step === "closed" && <ClosedStep reset={reset}/>}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------
function WelcomeStep({ goto, cfg }) {
  return (
    <div className="p-5 space-y-4">
      <Bubble>
        {cfg.greeting || "Hi! I'm Rigo, your Rescigo Health assistant. What would you like to do today?"}
      </Bubble>
      <div className="space-y-2">
        <ChoiceBtn testid="welcome-existing" onClick={() => goto("existing_phone", { kind: "existing" })}
                    icon={<User size={16} weight="fill"/>}
                    label="I'm an existing customer" hint="Login with phone + OTP"/>
        <ChoiceBtn testid="welcome-new" onClick={() => goto("new_phone", { kind: "new" })}
                    icon={<UserPlus size={16} weight="fill"/>}
                    label="I'm new — book a service" hint="Verify phone → talk to our team"/>
        <ChoiceBtn testid="welcome-ask" onClick={() => goto("ask_rigo")}
                    icon={<ChatCircleText size={16} weight="fill"/>}
                    label="Ask Rigo a quick question"
                    hint="Pickups, prices, bags, PINs — get instant answers"/>
      </div>
      <div className="text-[10px] text-[#8A8780] text-center pt-2">
        Chats are private and auto-delete after {cfg.retention_days || 30} days.
      </div>
    </div>
  );
}

function ChoiceBtn({ label, hint, icon, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#E5E2D9] bg-white hover:border-[#C25D38] hover:bg-[#FFF7F3] transition-colors text-left">
      <div className="w-9 h-9 rounded-full bg-[#C25D38] text-white flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#1A1A1A]">{label}</div>
        <div className="text-[11px] text-[#8A8780]">{hint}</div>
      </div>
      <ArrowRight size={14} className="text-[#8A8780]"/>
    </button>
  );
}

// --------------------------------------------------------------
function PhoneStep({ goto, ctx, kind }) {
  const [phone, setPhone] = useState(ctx.phone || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const send = async () => {
    setErr("");
    const clean = phone.replace(/\D/g, "");
    if (clean.length < 10) { setErr("Please enter a valid 10-digit mobile."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/chat/otp/send`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: clean, purpose: kind === "existing" ? "chat_existing" : "chat_new" }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.detail || "Could not send OTP"); return; }
      if (kind === "existing" && !d.is_existing) {
        setErr("No Rescigo account found for this number. Please choose 'I'm new' instead.");
        return;
      }
      goto(kind === "new" ? "new_otp" : "existing_otp",
           { phone: clean, dev_otp: d.dev_otp,
             is_existing: d.is_existing, customer_name: d.customer_name });
    } catch (e) { setErr("Network error — please try again"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 space-y-3">
      <Bubble>
        {kind === "new"
          ? "Great! Let's verify your mobile number so our team can reach you."
          : "Welcome back! Enter your registered mobile number to continue."}
      </Bubble>
      <label className="text-xs text-[#5C5A55]">Mobile number</label>
      <div className="flex items-center gap-2">
        <span className="h-11 px-3 border border-[#E5E2D9] rounded-lg text-sm bg-[#F0EFEA] text-[#5C5A55] flex items-center">
          +91
        </span>
        <input value={phone}
                inputMode="numeric" maxLength={10}
                onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                placeholder="10-digit mobile"
                data-testid={`chat-${kind}-phone-input`}
                className="flex-1 h-11 px-3 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#C25D38]"/>
      </div>
      {err && <div className="text-xs text-[#7A2E18]" data-testid={`chat-${kind}-phone-err`}>{err}</div>}
      <button onClick={send} disabled={busy}
              data-testid={`chat-${kind}-phone-send`}
              className="w-full h-11 rounded-lg bg-[#C25D38] hover:bg-[#A6462A] disabled:opacity-60 text-white text-sm font-medium">
        {busy ? "Sending OTP…" : "Send OTP"}
      </button>
      <p className="text-[10px] text-[#8A8780] text-center">
        By continuing you agree to be contacted about Rescigo services.
      </p>
    </div>
  );
}

// --------------------------------------------------------------
function OtpStep({ goto, ctx, kind }) {
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resent, setResent] = useState(false);

  const verify = async () => {
    setErr("");
    if (otp.length !== 6) { setErr("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/chat/otp/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: ctx.phone, otp, purpose: "chat" }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.detail || "Wrong OTP"); return; }
      if (kind === "existing") {
        goto("existing_menu", { customer: d.customer });
      } else {
        goto("new_details", {});
      }
    } catch (e) { setErr("Network error — please try again"); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    setResent(false);
    try {
      const r = await fetch(`${API}/public/chat/otp/send`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: ctx.phone, purpose: `chat_${kind}` }),
      });
      const d = await r.json();
      if (r.ok) { setResent(true); ctx.dev_otp = d.dev_otp; }
    } catch { /* silent */ }
  };

  return (
    <div className="p-5 space-y-3">
      <Bubble>
        Enter the 6-digit code we sent to <b>+91 {ctx.phone}</b>.
        {ctx.dev_otp && (
          <div className="mt-2 text-[10px] text-[#8A8780]">
            <span className="italic">Dev preview: <b className="tracking-widest">{ctx.dev_otp}</b></span>
          </div>
        )}
      </Bubble>
      <input value={otp}
              inputMode="numeric" maxLength={6}
              onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              data-testid={`chat-${kind}-otp-input`}
              className="w-full h-12 px-3 border border-[#E5E2D9] rounded-lg text-lg tracking-[0.5em] text-center outline-none focus:border-[#C25D38]"/>
      {err && <div className="text-xs text-[#7A2E18]" data-testid={`chat-${kind}-otp-err`}>{err}</div>}
      {resent && <div className="text-xs text-[#2D5A27]">A fresh code has been sent.</div>}
      <button onClick={verify} disabled={busy}
              data-testid={`chat-${kind}-otp-verify`}
              className="w-full h-11 rounded-lg bg-[#C25D38] hover:bg-[#A6462A] disabled:opacity-60 text-white text-sm font-medium">
        {busy ? "Verifying…" : "Verify & continue"}
      </button>
      <button onClick={resend} className="w-full text-xs text-[#5C5A55] hover:text-[#C25D38]"
              data-testid={`chat-${kind}-otp-resend`}>
        Resend code
      </button>
    </div>
  );
}

// --------------------------------------------------------------
function NewDetailsStep({ goto, ctx, setSession }) {
  const [name, setName]         = useState(ctx.customer_name || "");
  const [topic, setTopic]       = useState("new_service");
  const [area, setArea]         = useState("");
  const [msg, setMsg]           = useState("");
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");

  const submit = async () => {
    setErr("");
    if (!name.trim()) { setErr("Please share your name"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/chat/handoff`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(), phone: ctx.phone,
          topic, lsgd_hint: area.trim() || null,
          initial_message: msg.trim() || null,
          is_existing: false,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.detail || "Could not start chat"); return; }
      setSession({ token: d.session_token, conv_id: d.conversation_id });
      goto("queue", { name: name.trim(), topic });
    } catch (e) { setErr("Network error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 space-y-3">
      <Bubble>
        Tell me a bit more so I can hand you off to the right person.
      </Bubble>
      <label className="text-xs text-[#5C5A55]">Your name</label>
      <input value={name} onChange={e => setName(e.target.value)}
              placeholder="Full name" data-testid="chat-new-name"
              className="w-full h-11 px-3 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#C25D38]"/>
      <label className="text-xs text-[#5C5A55]">What do you need help with?</label>
      <div className="grid grid-cols-2 gap-2">
        {SERVICE_TOPICS.map(t => (
          <button key={t.id} onClick={() => setTopic(t.id)}
                  data-testid={`chat-new-topic-${t.id}`}
                  className={`h-10 px-2 rounded-lg text-xs border transition-colors ${
                    topic === t.id
                      ? "bg-[#C25D38] text-white border-[#C25D38]"
                      : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:border-[#C25D38]"
                  }`}>{t.label}</button>
        ))}
      </div>
      <label className="text-xs text-[#5C5A55]">Your area / pincode <span className="text-[#8A8780]">(optional)</span></label>
      <input value={area} onChange={e => setArea(e.target.value)}
              placeholder="e.g. Thiruvananthapuram / 695014" data-testid="chat-new-area"
              className="w-full h-11 px-3 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#C25D38]"/>
      <label className="text-xs text-[#5C5A55]">Message <span className="text-[#8A8780]">(optional)</span></label>
      <textarea value={msg} onChange={e => setMsg(e.target.value)}
                rows={2} placeholder="Anything you'd like to add?" data-testid="chat-new-msg"
                className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#C25D38]"/>
      {err && <div className="text-xs text-[#7A2E18]">{err}</div>}
      <button onClick={submit} disabled={busy}
              data-testid="chat-new-submit"
              className="w-full h-11 rounded-lg bg-[#C25D38] hover:bg-[#A6462A] disabled:opacity-60 text-white text-sm font-medium">
        {busy ? "Connecting…" : "Start chat with our team"}
      </button>
    </div>
  );
}

// --------------------------------------------------------------
function ExistingMenu({ goto, ctx, setSession }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const handoff = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/chat/handoff`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: ctx.customer?.name || "Customer",
          phone: ctx.customer?.phone || ctx.phone,
          existing_customer_id: ctx.customer?.id || null,
          is_existing: true,
          initial_message: msg.trim() || "Hi, I'd like help with something.",
        }),
      });
      const d = await r.json();
      if (r.ok) {
        setSession({ token: d.session_token, conv_id: d.conversation_id });
        goto("queue");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="p-5 space-y-3">
      <Bubble>
        Hi <b>{ctx.customer?.name || "there"}</b> 👋 What can I help you with?
      </Bubble>
      <div className="grid grid-cols-1 gap-2">
        {QUICK_LINKS_EXISTING.map(l => (
          l.handoff
            ? <div key={l.id}>
                <textarea rows={2} value={msg} onChange={e => setMsg(e.target.value)}
                          placeholder="Type your message for our team…"
                          data-testid="chat-existing-message"
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#C25D38] mb-2"/>
                <button onClick={handoff} disabled={busy}
                        data-testid={`chat-existing-${l.id}`}
                        className="w-full h-10 rounded-lg bg-[#C25D38] hover:bg-[#A6462A] disabled:opacity-60 text-white text-sm font-medium">
                  {busy ? "Connecting…" : l.label}
                </button>
              </div>
            : <a key={l.id} href={l.to}
                data-testid={`chat-existing-${l.id}`}
                className="block h-10 rounded-lg border border-[#E5E2D9] bg-white hover:border-[#C25D38] text-sm text-[#1A1A1A] flex items-center justify-between px-3">
                <span>{l.label}</span>
                <ArrowRight size={12}/>
              </a>
        ))}
      </div>
    </div>
  );
}

// --------------------------------------------------------------
function AskRigoStep({ cfg, goto }) {
  const [msgs, setMsgs] = useState([{ who: "bot", text: cfg.greeting }]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    // Iter 112 — robust auto-scroll: double-RAF sentinel-based jump
    const jump = () => {
      if (endRef.current) endRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
      else if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    };
    requestAnimationFrame(jump);
    requestAnimationFrame(() => requestAnimationFrame(jump));
  }, [msgs]);

  const send = async () => {
    const t = draft.trim();
    if (!t || busy) return;
    setMsgs(p => [...p, { who: "user", text: t }]);
    setDraft("");
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/chat/ask`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: t }),
      });
      const d = await r.json();
      setMsgs(p => [...p, { who: "bot", text: d.reply, escalate: d.escalate }]);
    } catch { setMsgs(p => [...p, { who: "bot", text: "Sorry, something went wrong." }]); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.map((m, i) => (
          m.who === "bot"
            ? <div key={i} className="flex items-end gap-2" data-testid="ask-rigo-msg-bot">
                <RigoAvatar size={26} ring={false} className="shrink-0"/>
                <div className="max-w-[82%]">
                  <div className="text-[10px] mb-0.5 flex items-center gap-1">
                    <span className="font-semibold text-[#1A1A1A]">Rigo</span>
                    <span className="px-1 rounded text-[9px] uppercase tracking-[0.1em] font-semibold bg-[#F0F7EE] text-[#2D5A27]">Bot</span>
                  </div>
                  <div className="rounded-2xl rounded-bl-none bg-white border border-[#E5E2D9] px-3 py-2 text-sm whitespace-pre-wrap">
                    {linkify(m.text)}
                  </div>
                  {m.escalate && (
                    <button onClick={() => goto("welcome")}
                            data-testid="ask-rigo-talk-human"
                            className="mt-2 inline-flex items-center gap-1 h-8 px-3 bg-[#C25D38] hover:bg-[#A6462A] text-white text-xs font-medium rounded-full">
                      Talk to a human <ArrowRight size={10} weight="bold"/>
                    </button>
                  )}
                </div>
              </div>
            : <div key={i} className="flex justify-end" data-testid="ask-rigo-msg-user">
                <div className="max-w-[80%] rounded-2xl rounded-br-none bg-[#C25D38] text-white px-3 py-2 text-sm">
                  {linkify(m.text)}
                </div>
              </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-[#8A8780]">
            <span className="w-2 h-2 rounded-full bg-[#C25D38] animate-bounce"/>Rigo is typing…
          </div>
        )}
        <div ref={endRef} data-testid="ask-rigo-end"/>
      </div>
      <div className="border-t border-[#E5E2D9] p-3 flex items-end gap-2 bg-white">
        <input value={draft} onChange={e => setDraft(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                placeholder="Type your question…" data-testid="ask-rigo-input"
                className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-full text-sm outline-none focus:border-[#C25D38]"/>
        <button onClick={send} disabled={busy || !draft.trim()}
                data-testid="ask-rigo-send"
                className="h-10 w-10 rounded-full bg-[#C25D38] hover:bg-[#A6462A] disabled:opacity-60 text-white flex items-center justify-center shrink-0">
          <PaperPlaneRight size={15} weight="fill"/>
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------
function LiveChatStep({ step, setStep, session, ctx }) {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("pending");
  const [queuePos, setQueuePos] = useState(null);
  const [assignedTo, setAssignedTo] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [waited, setWaited] = useState(0);       // seconds
  const scrollRef = useRef(null);
  const endRef = useRef(null);
  const sinceRef = useRef(null);
  const inputRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const [sRes, mRes] = await Promise.all([
        fetch(`${API}/public/chat/session?session_token=${session.token}`),
        fetch(`${API}/public/chat/session/messages?session_token=${session.token}${
          sinceRef.current ? `&since=${encodeURIComponent(sinceRef.current)}` : ""
        }`),
      ]);
      if (sRes.ok) {
        const s = await sRes.json();
        setStatus(s.status);
        setQueuePos(s.queue_position);
        setAssignedTo(s.assigned_staff_name);
        if (s.status === "human" && step === "queue") setStep("live");
        if (s.status === "closed") setStep("rating");
      }
      if (mRes.ok) {
        const list = await mRes.json();
        if (list.length) {
          setMessages(prev => {
            const seen = new Set(prev.map(m => m.id));
            const fresh = list.filter(m => !seen.has(m.id));
            if (!fresh.length) return prev;
            sinceRef.current = fresh[fresh.length - 1].created_at;
            return [...prev, ...fresh];
          });
        }
      }
    } catch { /* silent */ }
  }, [session, step, setStep]);

  useEffect(() => { poll(); const id = setInterval(poll, POLL_MS); return () => clearInterval(id); }, [poll]);

  // Iter 138 — Typing indicator (visitor side). Poll staff-typing every
  // POLL_MS; notify our own typing on input change (throttled 3s).
  const [staffTyping, setStaffTyping] = useState({ active: false, name: "" });
  const typingLastPostRef = useRef(0);
  useEffect(() => {
    if (!session?.token) return;
    let alive = true;
    const pollT = async () => {
      try {
        const r = await fetch(`${API}/public/chat/session/typing?session_token=${session.token}`);
        if (r.ok) {
          const d = await r.json();
          if (alive) setStaffTyping({ active: !!d.staff_typing, name: d.staff_name || "" });
        }
      } catch { /* ignore */ }
    };
    pollT();
    const iv = setInterval(pollT, POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, [session?.token]);
  const notifyTyping = () => {
    const now = Date.now();
    if (now - typingLastPostRef.current < 3000) return;
    typingLastPostRef.current = now;
    if (!session?.token) return;
    fetch(`${API}/public/chat/session/typing`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_token: session.token }),
    }).catch(() => {});
  };

  useEffect(() => {
    // Iter 112 — robust auto-scroll: double-RAF sentinel-based jump
    const jump = () => {
      if (endRef.current) endRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
      else if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    };
    requestAnimationFrame(jump);
    requestAnimationFrame(() => requestAnimationFrame(jump));
  }, [messages, status]);

  useEffect(() => {
    if (status !== "pending") return;
    const id = setInterval(() => setWaited(w => w + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const send = async () => {
    const t = draft.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/chat/session/messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_token: session.token, text: t }),
      });
      if (r.ok) { setDraft(""); await poll(); }
    } finally {
      setBusy(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const leave = async () => {
    await fetch(`${API}/public/chat/session/close?session_token=${session.token}`, { method: "POST" });
    setStep("rating");
  };

  const queueBanner = (() => {
    if (status !== "pending") return null;
    if (waited < 60) {
      return { tone: "amber",
        text: `You're in the queue${queuePos ? ` at position #${queuePos}` : ""}. Estimated wait: under a minute.` };
    }
    if (waited < 120) {
      return { tone: "amber",
        text: "Thanks for holding! Our team is finishing up with another customer — should be with you shortly." };
    }
    return { tone: "red",
      text: "Our team is heavily loaded right now. You can keep waiting or leave a message and we'll get back to you." };
  })();

  return (
    <div className="flex flex-col h-full">
      {/* Status strip */}
      <div className={`px-3 py-2 text-xs border-b ${
        status === "human"
          ? "bg-[#EDF7EA] border-[#B5D5AC] text-[#1A4E12]"
          : status === "pending"
            ? (queueBanner?.tone === "red"
                ? "bg-[#FBEBE5] border-[#EFC7B6] text-[#7A2E18]"
                : "bg-[#FDF6E4] border-[#F0DDB2] text-[#5A4600]")
            : "bg-[#F0EFEA] border-[#E5E2D9] text-[#5C5A55]"
      }`}
           data-testid="live-status-strip">
        {status === "human" && (
          <>🟢 Live with <b>{assignedTo || "our team"}</b></>
        )}
        {status === "pending" && queueBanner && (<>⏳ {queueBanner.text}</>)}
        {status === "closed" && (<>Chat closed</>)}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFAF7]"
           data-testid="live-messages">
        {messages.map(m => <MsgBubble key={m.id} m={m}/>)}
        {/* Iter 138 — staff typing indicator */}
        {staffTyping.active && (
          <div className="flex items-center gap-2 text-[11px] text-[#5C5A55] italic"
               data-testid="pub-staff-typing">
            <span className="typing-dot-pulse inline-flex gap-0.5">
              <span/><span/><span/>
            </span>
            {staffTyping.name || "Rescigo Team"} is typing…
          </div>
        )}
        {status === "pending" && waited > 60 && (
          <button onClick={leave}
                  data-testid="live-leave-btn"
                  className="mx-auto block text-xs text-[#7A2E18] underline">
            Leave chat and get a call-back
          </button>
        )}
        <div ref={endRef} data-testid="live-messages-end"/>
      </div>

      {/* Composer — always available so visitor can add context while queued */}
      <div className="border-t border-[#E5E2D9] p-3 flex items-end gap-2 bg-white">
        <input value={draft} onChange={e => { setDraft(e.target.value); if (e.target.value) notifyTyping(); }}
                ref={inputRef}
                autoFocus
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                placeholder={status === "human" ? "Reply to our team…" : "Add more details…"}
                data-testid="live-input"
                className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-full text-sm outline-none focus:border-[#C25D38]"/>
        <button onClick={send} disabled={busy || !draft.trim()}
                data-testid="live-send"
                className="h-10 w-10 rounded-full bg-[#C25D38] hover:bg-[#A6462A] disabled:opacity-60 text-white flex items-center justify-center shrink-0">
          <PaperPlaneRight size={15} weight="fill"/>
        </button>
      </div>
    </div>
  );
}

function MsgBubble({ m }) {
  if (m.sender === "system") {
    return (
      <div className="text-center text-[10px] text-[#8A8780] italic">
        {linkify(m.text)}
      </div>
    );
  }
  const isVisitor = m.sender === "customer";
  if (isVisitor) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-none bg-[#C25D38] text-white px-3 py-2 text-sm">
          {linkify(m.text)}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-end gap-2">
      {m.sender === "bot"
        ? <RigoAvatar size={26} ring={false} className="shrink-0"/>
        : <div className="w-[26px] h-[26px] rounded-full bg-[#2D5A27] text-white text-[10px] flex items-center justify-center shrink-0">
            {(m.sender_name || "R")[0].toUpperCase()}
          </div>}
      <div className="max-w-[82%]">
        <div className="text-[10px] mb-0.5 flex items-center gap-1">
          <span className="font-semibold text-[#1A1A1A]">{m.sender_name || (m.sender === "bot" ? "Rigo" : "Rescigo Team")}</span>
          <span className={`px-1 rounded text-[9px] uppercase tracking-[0.1em] font-semibold ${
            m.sender === "bot"
              ? "bg-[#F0F7EE] text-[#2D5A27]"
              : "bg-[#DDE7F1] text-[#1F3A57]"
          }`}>{m.sender === "bot" ? "Bot" : "Team"}</span>
        </div>
        <div className="rounded-2xl rounded-bl-none bg-white border border-[#E5E2D9] px-3 py-2 text-sm whitespace-pre-wrap">
          {linkify(m.text)}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------
function RatingStep({ session, onDone }) {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!rating) { onDone(); return; }
    setBusy(true);
    try {
      await fetch(`${API}/public/chat/session/rating`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_token: session.token,
                                rating, feedback: feedback.trim() || null }),
      });
    } finally { setBusy(false); onDone(); }
  };

  return (
    <div className="p-5 space-y-4">
      <Bubble>Thanks for chatting with us! How was your experience?</Bubble>
      <div className="flex items-center justify-center gap-2">
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={() => setRating(n)}
                  data-testid={`chat-rating-${n}`}
                  className="p-1">
            <Star size={30} weight={n <= rating ? "fill" : "regular"}
                   className={n <= rating ? "text-[#F5B841]" : "text-[#C7C4BB]"}/>
          </button>
        ))}
      </div>
      <textarea value={feedback} onChange={e => setFeedback(e.target.value)}
                rows={2} placeholder="Anything you'd like to add? (optional)"
                data-testid="chat-rating-feedback"
                className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#C25D38]"/>
      <button onClick={submit} disabled={busy}
              data-testid="chat-rating-submit"
              className="w-full h-11 rounded-lg bg-[#C25D38] hover:bg-[#A6462A] disabled:opacity-60 text-white text-sm font-medium">
        {busy ? "Submitting…" : (rating ? "Submit rating" : "Skip")}
      </button>
    </div>
  );
}

// --------------------------------------------------------------
function ClosedStep({ reset }) {
  return (
    <div className="p-8 flex flex-col items-center text-center gap-3">
      <CheckCircle size={44} weight="fill" className="text-[#2D5A27]"/>
      <div className="font-heading text-lg">Chat closed</div>
      <div className="text-sm text-[#5C5A55] max-w-xs">
        Thanks for reaching out! Our team may follow up on your registered mobile. You can start a fresh chat anytime.
      </div>
      <button onClick={reset}
              data-testid="chat-closed-restart"
              className="mt-2 h-10 px-5 rounded-lg bg-[#C25D38] hover:bg-[#A6462A] text-white text-sm font-medium">
        Start a new chat
      </button>
    </div>
  );
}

// --------------------------------------------------------------
function Bubble({ children }) {
  return (
    <div className="flex items-start gap-2">
      <RigoAvatar size={28} ring={false} className="shrink-0"/>
      <div className="rounded-2xl rounded-bl-none bg-white border border-[#E5E2D9] px-3 py-2 text-sm text-[#1A1A1A]">
        {children}
      </div>
    </div>
  );
}
